import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import { CheckCircle, Circle, Clock, XCircle, ArrowRight, RefreshCw } from 'lucide-react';

const STEPS = [
  { key: 'registered', label: 'Registered' },
  { key: 'profile', label: 'Profile Completed' },
  { key: 'applied', label: 'Application Submitted' },
  { key: 'review', label: 'Under Review' },
  { key: 'decision', label: 'Decision' },
];

export default function MembershipStatus() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/membership/status').then(r => setStatus(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (user?.role === 'member') { navigate('/dashboard'); return null; }
  if (user?.role === 'admin') { navigate('/admin'); return null; }

  const getStepStatus = (step) => {
    const ms = status?.membership_status;
    const pc = status?.profile_completed;
    if (step.key === 'registered') return 'done';
    if (step.key === 'profile') return pc ? 'done' : 'current';
    if (step.key === 'applied') {
      if (!pc) return 'future';
      if (!ms || ms === 'none') return 'current';
      return 'done';
    }
    if (step.key === 'review') {
      if (!ms || ms === 'none') return 'future';
      if (ms === 'pending') return 'current';
      return 'done';
    }
    if (step.key === 'decision') {
      if (ms === 'approved') return 'approved';
      if (ms === 'rejected') return 'rejected';
      return 'future';
    }
    return 'future';
  };

  const StepIcon = ({ stepStatus }) => {
    if (stepStatus === 'done' || stepStatus === 'approved') return <CheckCircle size={20} style={{ color: 'var(--gold)' }} />;
    if (stepStatus === 'rejected') return <XCircle size={20} style={{ color: 'var(--danger)' }} />;
    if (stepStatus === 'current') return <Clock size={20} style={{ color: 'var(--gold)' }} />;
    return <Circle size={20} style={{ color: 'var(--border-clr)' }} />;
  };

  return (
    <div style={{ backgroundColor: 'var(--bg-subtle)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container-app py-10">
        <div className="max-w-lg mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Membership Status</h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Track the progress of your membership application.</p>
          </div>

          {/* Status card */}
          <div className="p-8 rounded-2xl border mb-6" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }} data-testid="membership-status-card">
            {/* Steps */}
            <div className="space-y-4 mb-6">
              {STEPS.map((step, i) => {
                const stepStatus = getStepStatus(step);
                return (
                  <div key={step.key} className="flex items-center gap-4" data-testid="membership-status-step">
                    <StepIcon stepStatus={stepStatus} />
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: stepStatus === 'future' ? 'var(--text-muted)' : 'var(--text)' }}>{step.label}</p>
                    </div>
                    {stepStatus === 'current' && (
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--gold-pale)', color: 'var(--gold)' }}>In progress</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Current status message */}
            {status?.membership_status === 'pending' && (
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--gold-pale)', border: '1px solid var(--gold-soft)' }}>
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Your application is under review</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>We typically respond within 1–3 business days. You’ll receive an email once a decision has been made.</p>
              </div>
            )}

            {status?.membership_status === 'rejected' && (
              <div>
                <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }}>
                  <p className="text-sm font-medium" style={{ color: '#991B1B' }}>Application not approved</p>
                  {status?.membership_admin_notes && (
                    <p className="text-xs mt-1" style={{ color: '#7F1D1D' }}><strong>Note:</strong> {status.membership_admin_notes}</p>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button size="sm" onClick={() => navigate('/profile/complete')} variant="outline" style={{ borderColor: 'var(--border-strong)' }}>Update Profile</Button>
                  <Button size="sm" onClick={() => navigate('/membership/apply')} style={{ backgroundColor: 'var(--gold)', color: 'white', borderRadius: '8px' }}>
                    <RefreshCw size={13} className="mr-1.5" /> Reapply
                  </Button>
                </div>
              </div>
            )}

            {(!status?.membership_status || status?.membership_status === 'none') && !status?.profile_completed && (
              <Button className="w-full" onClick={() => navigate('/profile/complete')} style={{ backgroundColor: 'var(--text)', color: 'white', borderRadius: '10px' }}>
                Complete Profile <ArrowRight size={15} className="ml-2" />
              </Button>
            )}

            {(!status?.membership_status || status?.membership_status === 'none') && status?.profile_completed && (
              <Button className="w-full" onClick={() => navigate('/membership/apply')} style={{ backgroundColor: 'var(--gold)', color: 'white', borderRadius: '10px' }}>
                Submit Application <ArrowRight size={15} className="ml-2" />
              </Button>
            )}
          </div>

          <p className="text-center text-xs" style={{ color: 'var(--text-muted)' }}>Questions? Contact us at <a href="mailto:info@thelistingroom.com" style={{ color: 'var(--gold)' }}>info@thelistingroom.com</a></p>
        </div>
      </div>
    </div>
  );
}
